import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { LoadingCrossfade } from '../../components/loading-skeleton/index';
import { ModalWrapper } from '../../components/modal-wrapper/index';
import { CrossfadePanel } from '../../components/motion/index';
import { PaginationController } from '../../components/pagination-controller/index';
import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import {
  AnalyticHooks,
  Pool,
  SharePlan,
  useAppMutation,
  useHasLoadedOnce,
  useModalState,
  useQueryParams,
  useSelectedCompany,
} from '../../hooks/index';
import { ApiService } from '../../services/index';
import { PossibleAction } from '../../types/google-analytics-types.types';
import {
  ExerciseType,
  itemsToFetchMap,
  PoolPlansTab,
  PoolsPlansFilter,
  SelectVariant,
} from '../../types/pool-plans.types';
import { AdjustPoolSizeModal } from './adjust-pool-size/index';
import { ExerciseForm } from './modals/exercise/exercise-form/index';
import { EditPlanModal, PlanTerminationDateModal } from './plan-form/edit/index';
import { AddPlanForm } from './plan-form/index';
import { EditPlanStepVariant } from './plan-form/plan-form.types';
import {
  AddPoolForm,
  EditPoolModal,
  EditShareClassStepVariant,
  PoolTerminationDateModal,
} from './pool-form/index';
import PoolPlansHeader from './pool-plans-header';
import { PoolPlansPlaceholder, PoolPlansTablePlaceholder } from './pool-plans-loading-placeholders';
import { PoolPlansMobile } from './pool-plans-mobile/pool-plans-mobile';
import { PoolPlansCommonTable } from './pool-plans-table/index';

export type SelectedShareClass = {
  id: string;
  mode: 'EDIT' | 'VIEW';
  step: EditShareClassStepVariant;
};

export type SelectedPlan = {
  id: string;
  mode: 'EDIT' | 'VIEW';
  step: EditPlanStepVariant;
};

const PoolPlans: FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<PoolsPlansFilter>(PoolsPlansFilter.BY_POOLS);
  const [selected, setSelected] = useState<SelectVariant>();
  const [vestByDate, setVestByDate] = useState<Date>(new Date());
  const { selectedCompany } = useSelectedCompany();
  const [selectedTab, setTab] = useState<PoolPlansTab>(PoolPlansTab.ACTIVE);
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfFetchedItems, setNumberOfFetchedItems] = useState(itemsToFetchMap[0]);

  const [selectedPool, setSelectedPool] = useState<null | SelectedShareClass>({
    id: '',
    step: 1,
    mode: 'EDIT',
  });

  const [selectedPlan, setSelectedPlan] = useState<null | SelectedPlan>({
    id: '',
    step: 1,
    mode: 'EDIT',
  });
  const handleFilterSelect = (filter: PoolsPlansFilter) => {
    setCurrentPage(1);
    setSelectedFilter(filter);
    setSelectedPool(null);
    setSelectedPlan(null);
  };
  const companyId = selectedCompany?.id || '';

  const { get } = useQueryParams();

  const {
    handleCloseModal: handleCloseAddPoolModal,
    handleOpenModal: handleOpenAddPoolModal,
    isOpen: isOpenAddPoolModal,
    toggler: AddPoolModalToggler,
  } = useModalState({ initialValue: !!get('openAddModal') });

  const {
    handleCloseModal: handleCloseEditPoolModal,
    handleOpenModal: handleOpenEditPoolModal,
    isOpen: isOpenEditPoolModal,
  } = useModalState({});
  const queryClient = useQueryClient();

  const { pools, totalPages, isLoading } = Pool.usePools({
    companyId,
    currentPage,
    take: Number(numberOfFetchedItems),
    vestByDate,
  });
  const {
    pools: terminatedPools,
    totalPages: terminatedTotalPages,
    isLoading: isTerminatedLoading,
  } = Pool.useTerminatedPools({
    companyId,
    currentPage,
    take: Number(numberOfFetchedItems),
  });
  const { terminate: terminatePool } = Pool.useTerminate();
  const companyDeletePoolTracker = AnalyticHooks.useClick(PossibleAction.DELETE_POOL);
  const { deletePool } = Pool.useDelete();
  const { terminate: terminatePlan } = SharePlan.useTerminate();
  const deleteSharePlanTracker = AnalyticHooks.useClick(PossibleAction.DELETE_PLAN);
  const { deletePlan } = SharePlan.useDelete();

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_POOLS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_POOL] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_TERMINATED_POOLS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SHARE_PLANS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SHARE_PLAN] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.SHARE_PLAN_STAKEHOLDERS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_STAKEHOLDERS_WITH_GRANTS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_POOLS_BY_STAKEHOLDER] });
  }, [queryClient]);

  const {
    isOpen: isOpenPlanModal,
    handleOpenModal: handleOpenPlanModal,
    handleCloseModal: handleClosePlanModal,
    toggler: PlanModalToggler,
  } = useModalState({ initialValue: !!get('openAddPlanModal') });

  const {
    handleCloseModal: handleCloseEditPlanModal,
    handleOpenModal: handleOpenEditPlanModal,
    isOpen: isOpenEditPlanModal,
  } = useModalState({ initialValue: !!get('openEditPlanModal') });

  const paramPlanId = get('paramPlanId');

  const {
    handleCloseModal: handleClosePoolTerminationDateModal,
    handleOpenModal: handleOpenPoolTerminationDateModal,
    isOpen: isOpenPoolTerminationDateModal,
  } = useModalState({});

  const {
    handleCloseModal: handleClosePlanTerminationDateModal,
    handleOpenModal: handleOpenPlanTerminationDateModal,
    isOpen: isOpenPlanTerminationDateModal,
  } = useModalState({});

  const {
    handleCloseModal: handleCloseExerciseStockModal,
    handleOpenModal: handleOpenExerciseStockModal,
    isOpen: isOpenExerciseStockModal,
    modalData: exerciseType,
  } = useModalState<unknown, unknown, ExerciseType>();

  const { mutate: mutateExport } = useAppMutation([QueryKey.EXPORT_POOLS_PLANS, companyId], {
    mutationFn: async () => {
      if (!selectedCompany) return;

      await ApiService.downloadFile({
        endpoint: BackendRoute.COMPANIES,
        routePath: [selectedCompany?.id || '', 'pools-plans-export'],
      });
    },
  });

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber);
      invalidateQuery();
    },
    [invalidateQuery],
  );

  const handleSelectNumberOfFetchedItems = useCallback(
    (select: number) => {
      setNumberOfFetchedItems(select);
      invalidateQuery();
    },
    [invalidateQuery],
  );

  const {
    stakeholders,
    totalPages: stakeholdersTotalPages,
    isLoading: stakeholdersLoading,
  } = Pool.useStakeholdersWithGrants({
    companyId,
    currentPage,
    take: Number(numberOfFetchedItems),
    vestByDate,
  });

  const openEditPlanModal = get('openEditPlanModal');

  useEffect(() => {
    const isOpen = !!openEditPlanModal;

    if (isOpen && paramPlanId) {
      handleOpenEditPlanModal();
    } else {
      handleCloseEditPlanModal();
    }
  }, [handleCloseEditPlanModal, handleOpenEditPlanModal, openEditPlanModal, paramPlanId]);

  const isLastItemOnPage = pools?.length === 1;
  const isFirstPage = currentPage === 1;

  const isPoolsDataLoading =
    !pools ||
    !terminatedPools ||
    isLoading ||
    isTerminatedLoading ||
    !stakeholders ||
    stakeholdersLoading;

  // Full skeleton on first load only; later pagination / vest-date reloads skeleton the table.
  const hasLoadedOnce = useHasLoadedOnce(isPoolsDataLoading);

  return (
    <LoadingCrossfade
      isLoading={isPoolsDataLoading && !hasLoadedOnce}
      skeleton={<PoolPlansPlaceholder />}
    >
      {() => (
        <>
          <ModalWrapper
            control={{ open: isOpenPlanModal, onOpenChange: PlanModalToggler }}
            side="RIGHT"
          >
            <AddPlanForm
              closeModal={handleClosePlanModal}
              companyId={companyId}
              invalidateQuery={invalidateQuery}
            />
          </ModalWrapper>
          <ModalWrapper
            className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
            control={{ open: isOpenAddPoolModal, onOpenChange: AddPoolModalToggler }}
            side="RIGHT"
          >
            <AddPoolForm
              companyId={companyId}
              handleClose={handleCloseAddPoolModal}
              invalidateQuery={invalidateQuery}
            />
          </ModalWrapper>
          <EditPoolModal
            companyId={companyId}
            handleClose={handleCloseEditPoolModal}
            handleTerminate={({ date, id }) =>
              terminatePool({ date, poolId: id, companyId }, { onSuccess: invalidateQuery })
            }
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenEditPoolModal}
            onEditClick={({ id, step }) => setSelectedPool({ id, mode: 'EDIT', step })}
            poolId={selectedPool?.id || ''}
            previewMode={selectedPool?.mode === 'VIEW' || undefined}
            step={selectedPool?.step || 1}
          />
          <EditPlanModal
            companyId={companyId}
            handleClose={handleCloseEditPlanModal}
            handleTerminate={({ date, id }) =>
              terminatePlan({ date, planId: id, companyId }, { onSuccess: invalidateQuery })
            }
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenEditPlanModal}
            onEditClick={({ id, step }) => setSelectedPlan({ id, mode: 'EDIT', step })}
            planId={selectedPlan?.id || paramPlanId || ''}
            previewMode={selectedPlan?.mode === 'VIEW' || undefined}
            step={paramPlanId ? 2 : selectedPlan?.step || 1}
          />
          <PoolTerminationDateModal
            companyId={companyId}
            handleClose={handleClosePoolTerminationDateModal}
            handleTerminate={({ date, id }) =>
              terminatePool({ companyId, date, poolId: id }, { onSuccess: invalidateQuery })
            }
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenPoolTerminationDateModal}
            poolId={selectedPool?.id || ''}
          />
          <PlanTerminationDateModal
            companyId={companyId}
            handleClose={handleClosePlanTerminationDateModal}
            handleTerminate={({ date, id }) =>
              terminatePlan({ companyId, date, planId: id }, { onSuccess: invalidateQuery })
            }
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenPlanTerminationDateModal}
            planId={selectedPlan?.id || ''}
          />
          <ExerciseForm
            companyId={companyId}
            handleClose={handleCloseExerciseStockModal}
            invalidateQuery={invalidateQuery}
            isOpen={isOpenExerciseStockModal}
            type={exerciseType}
          />
          <AdjustPoolSizeModal companyId={companyId} />
          <div className="flex h-fit min-h-full w-full flex-col gap-4 overflow-x-auto rounded-md lg:p-4 lg:shadow-sm">
            <div className="flex h-[36px] w-full gap-4 border-b-[1px] border-gray-200">
              <Button
                className={twMerge(
                  'flex h-full w-fit rounded-none border-b-2 border-transparent text-sm font-[450] text-gray-400',
                  selectedTab === PoolPlansTab.ACTIVE &&
                    'border-brand-700 font-[550] text-brand-700',
                )}
                onClick={() => {
                  setTab(PoolPlansTab.ACTIVE);
                  setCurrentPage(1);
                }}
                styleType="NONE"
              >
                <AppFormattedMessage id="ACTIVE" />
              </Button>
              <Button
                className={twMerge(
                  'flex h-full w-fit rounded-none border-b-2 border-transparent text-sm font-[450] text-gray-400',
                  selectedTab === PoolPlansTab.TERMINATED &&
                    'border-brand-700 font-[550] text-brand-700',
                )}
                onClick={() => {
                  setTab(PoolPlansTab.TERMINATED);
                  setCurrentPage(1);
                  setSelectedFilter(PoolsPlansFilter.BY_POOLS);
                }}
                styleType="NONE"
              >
                <AppFormattedMessage id="TERMINATED" />
              </Button>
            </div>
            <AnimatePresence mode="wait">
              <CrossfadePanel
                className="flex min-h-full w-full flex-1 flex-col gap-4"
                key={`${selectedTab}-${selectedFilter}`}
                panelKey={`${selectedTab}-${selectedFilter}`}
                translate={false}
              >
                <div className="hidden h-fit min-h-full w-full flex-1 flex-col rounded-md border-gray-200 py-4 shadow-sm xlg:flex">
                  <PoolPlansHeader
                    date={vestByDate}
                    onAddPlanClick={handleOpenPlanModal}
                    onAddPoolClick={handleOpenAddPoolModal}
                    onExportClick={mutateExport}
                    onOpenExerciseStockModal={handleOpenExerciseStockModal}
                    pools={selectedTab === PoolPlansTab.ACTIVE ? pools : terminatedPools}
                    selected={selected}
                    selectedFilter={selectedFilter}
                    selectedTab={selectedTab}
                    setDate={setVestByDate}
                    setSelected={setSelected}
                    setSelectedFilter={handleFilterSelect}
                  />

                  <AnimatePresence mode="wait">
                    <CrossfadePanel
                      className="flex w-full flex-1 flex-col"
                      key={isPoolsDataLoading ? 'pools-loading' : 'pools-ready'}
                      panelKey={isPoolsDataLoading ? 'pools-loading' : 'pools-ready'}
                      translate={false}
                    >
                      {isPoolsDataLoading ? (
                        <PoolPlansTablePlaceholder />
                      ) : (
                        <PoolPlansCommonTable
                          date={vestByDate}
                          isSortedByPools={selectedFilter === PoolsPlansFilter.BY_POOLS}
                          onAddPlanClick={handleOpenPlanModal}
                          onAddPoolClick={handleOpenAddPoolModal}
                          onDeleteClick={(id) => {
                            deletePool(
                              { companyId, poolId: id },
                              {
                                onSuccess: () => {
                                  companyDeletePoolTracker();
                                  if (isLastItemOnPage && !isFirstPage) {
                                    setCurrentPage((prev) => prev - 1);
                                  }
                                  invalidateQuery();
                                  setSelectedPool(null);
                                },
                              },
                            );
                          }}
                          onDeletePlanClick={(id) => {
                            deletePlan(
                              { companyId, planId: id },
                              {
                                onSuccess: () => {
                                  deleteSharePlanTracker();
                                  toast.success(
                                    <AppFormattedMessage id="PLAN_SUCCESSFULLY_DELETED" />,
                                  );
                                  invalidateQuery();
                                  setSelectedPlan(null);
                                },
                              },
                            );
                          }}
                          onEditClick={(id) => {
                            handleOpenEditPoolModal();
                            setSelectedPool({
                              id,
                              mode: 'EDIT',
                              step: 3,
                            });
                          }}
                          onEditPlanClick={(id) => {
                            handleOpenEditPlanModal();
                            setSelectedPlan({
                              id,
                              mode: 'EDIT',
                              step: 5,
                            });
                          }}
                          onEditPlanTerminationDateClick={(id) => {
                            handleOpenPlanTerminationDateModal();
                            setSelectedPlan({
                              id,
                              mode: 'EDIT',
                              step: 3,
                            });
                          }}
                          onEditTerminationDateClick={(id) => {
                            handleOpenPoolTerminationDateModal();
                            setSelectedPool({
                              id,
                              mode: 'EDIT',
                              step: 3,
                            });
                          }}
                          onViewClick={(id) => {
                            handleOpenEditPoolModal();

                            setSelectedPool({
                              id,
                              mode: 'VIEW',
                              step: 3,
                            });
                          }}
                          onViewPlanClick={(id) => {
                            handleOpenEditPlanModal();

                            setSelectedPlan({
                              id,
                              mode: 'VIEW',
                              step: 5,
                            });
                          }}
                          pools={selectedTab === PoolPlansTab.ACTIVE ? pools : terminatedPools}
                          selectedTab={selectedTab}
                          stakeholders={stakeholders}
                        />
                      )}
                    </CrossfadePanel>
                  </AnimatePresence>
                  <div className="mt-auto flex items-center justify-between py-4 pl-6">
                    <PaginationController
                      className="max-lg:px-4 lg:mx-6 lg:ml-auto"
                      currentPage={currentPage}
                      itemsToFetch={numberOfFetchedItems}
                      itemsToFetchMap={itemsToFetchMap}
                      onClick={handlePageChange}
                      onFetchAmountChange={handleSelectNumberOfFetchedItems}
                      totalPages={
                        selectedTab === PoolPlansTab.ACTIVE
                          ? selectedFilter === PoolsPlansFilter.BY_STAKEHOLDER
                            ? stakeholdersTotalPages || 0
                            : totalPages || 0
                          : terminatedTotalPages || 0
                      }
                    />
                  </div>
                </div>
                <div className="flex w-full flex-col border-gray-200 xlg:hidden">
                  <PoolPlansMobile
                    companyId={companyId}
                    currentPage={currentPage}
                    date={vestByDate}
                    handlePageChange={handlePageChange}
                    selectedTab={selectedTab}
                  />
                </div>
              </CrossfadePanel>
            </AnimatePresence>
          </div>
        </>
      )}
    </LoadingCrossfade>
  );
};

export default PoolPlans;
